import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { ContactLabelSelector } from './ContactLabelSelector';
import { ContactCategorySelector } from './ContactCategorySelector';
import { useCreditConfirm } from './credits/useCreditConfirm';

interface TinyAutomation {
  id: string;
  name: string;
  enabled: boolean;
  sessionNames: string[];
  situacao: string;
  filterType: string;
  filterValues: string[];
  messageType: string;
  messageContent: any;
  randomDelay: number;
  randomDelayMax: number | null;
  scheduleStartDay: number | null;
  scheduleEndDay: number | null;
  scheduleStartHour: number | null;
  scheduleEndHour: number | null;
  categoryId: string | null;
  contactTags?: string[];
  category: { id: string; nome: string; cor: string } | null;
  lateAccountId?: string | null;
  lateTemplateName?: string | null;
  lateTemplateLang?: string | null;
}

interface LateAccount {
  id: string;
  platformAccountId: string;
  accountName: string;
  accountUsername?: string;
  groupName?: string;
}

interface LateTemplate {
  id: string;
  name: string;
  language: string;
  status: string;
  components?: Array<{ type: string; text?: string }>;
  variableOrder?: string[];
}

interface WhatsAppSession {
  name: string;
  displayName?: string;
  status: string;
}

interface Category {
  id: string;
  nome: string;
  cor?: string;
}

const DAY_NAMES = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
const ERP_TEMPLATE_VARIABLE_KEYS = new Set(['nome', 'primeironome', 'itens', 'ecommerce', 'numeropedido', 'numeronotafiscal', 'telefone']);
const CONTACT_ONLY_TEMPLATE_VARIABLE_KEYS = new Set(['email', 'observacoes', 'categoria']);

function extractVariableOrder(text: string): string[] {
  const seen = new Set<string>();
  const order: string[] = [];
  const re = /\{\{\s*([^}]+?)\s*\}\}/gi;
  let m;
  while ((m = re.exec(text)) !== null) {
    const key = m[1].trim().toLowerCase();
    if (/^\d+$/.test(key)) continue;
    if (!seen.has(key)) { seen.add(key); order.push(key); }
  }
  return order;
}

const TINY_VARIABLE_LABELS: Record<string, string> = {
  nome: 'Nome do cliente',
  primeironome: 'Primeiro nome',
  itens: 'Itens do pedido',
  ecommerce: 'Nome do e-commerce',
  numeropedido: 'Nº Pedido',
  numeronotafiscal: 'Nº Nota Fiscal',
  telefone: 'Telefone',
  email: 'E-mail',
  observacoes: 'Observações',
  categoria: 'Categoria',
};

const MESSAGE_TYPES = [
  { value: 'text', label: '💬 Texto' },
  { value: 'image', label: '🖼️ Imagem' },
  { value: 'video', label: '🎬 Vídeo' },
  { value: 'audio', label: '🎵 Áudio' },
  { value: 'document', label: '📄 Arquivo' },
  { value: 'openai', label: '🤖 OpenAI' },
  { value: 'wait', label: '⏱️ Espera' },
];

const getDefaultContent = (type: string) => {
  switch (type) {
    case 'text': return { text: '' };
    case 'openai': return { model: 'cx/gpt-6-luna', system: '', user: '' };
    case 'wait': return { waitTime: 30 };
    case 'document': return { url: '', caption: '', fileName: '' };
    default: return { url: '', caption: '' };
  }
};

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileIcon = (type: string) => {
  if (type.startsWith('image/')) return '🖼️';
  if (type.startsWith('video/')) return '🎥';
  if (type.startsWith('audio/')) return '🎵';
  return '📄';
};

export function TinyAutomationCard({ authenticatedFetch }: { authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response> }) {
  const { request: requestCreditConfirm, modal: creditConfirmModal } = useCreditConfirm();
  const [automations, setAutomations] = useState<TinyAutomation[]>([]);
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState<WhatsAppSession[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [formName, setFormName] = useState('');
  const [formEnabled, setFormEnabled] = useState(false);
  const [formSessionNames, setFormSessionNames] = useState<string[]>([]);
  const [formSituacao, setFormSituacao] = useState('entregue');
  const [formFilterType, setFormFilterType] = useState<'descricao' | 'sku'>('descricao');
  const [formFilterValues, setFormFilterValues] = useState<string[]>(['']);
  const [formSequence, setFormSequence] = useState<Array<{ type: string; content: any }>>([]);
  const [uploadingFiles, setUploadingFiles] = useState<{ [key: number]: boolean }>({});
  const [fileInfos, setFileInfos] = useState<{ [key: number]: { name: string; size: number; type: string } }>({});
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [formRandomDelay, setFormRandomDelay] = useState(30);
  const [formRandomDelayMax, setFormRandomDelayMax] = useState<number | null>(80);
  const [formScheduleStartDay, setFormScheduleStartDay] = useState<number | null>(null);
  const [formScheduleEndDay, setFormScheduleEndDay] = useState<number | null>(null);
  const [formScheduleStartHour, setFormScheduleStartHour] = useState<number | null>(null);
  const [formScheduleEndHour, setFormScheduleEndHour] = useState<number | null>(null);
  const [formCategoryId, setFormCategoryId] = useState<string>('');
  const [formContactTags, setFormContactTags] = useState<string[]>([]);

  // WhatsApp Oficial (GetLate)
  const [useOfficialApi, setUseOfficialApi] = useState(false);
  const [lateAccounts, setLateAccounts] = useState<LateAccount[]>([]);
  const [formLateAccountId, setFormLateAccountId] = useState<string>('');
  const [lateTemplates, setLateTemplates] = useState<LateTemplate[]>([]);
  const [formLateTemplateName, setFormLateTemplateName] = useState<string>('');
  const [formLateTemplateLang, setFormLateTemplateLang] = useState<string>('pt_BR');
  const [formTemplateText, setFormTemplateText] = useState<string>('');
  const [formTemplateVarOrder, setFormTemplateVarOrder] = useState<string[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const selectedLateTemplate = lateTemplates.find(template => template.name === formLateTemplateName) || null;
  const selectedTemplateIncompatibleVariables = selectedLateTemplate ? getTemplateIncompatibleVariables(selectedLateTemplate) : [];
  const selectedTemplateUnknownVariables = selectedLateTemplate ? getTemplateUnknownVariables(selectedLateTemplate) : [];

  // DirectData token (global)
  const [hasDirectDataToken, setHasDirectDataToken] = useState(false);
  const [processingNow, setProcessingNow] = useState(false);

  const loadAutomations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authenticatedFetch('/api/tiny/automations');
      const data = await res.json();
      setAutomations(data.automations || []);
    } catch (error) {
      console.error('Erro ao carregar automações:', error);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  const loadSessions = useCallback(async () => {
    try {
      const res = await authenticatedFetch('/api/campaigns/sessions');
      const data = await res.json();
      setSessions(data || []);
    } catch (error) {
      console.error('Erro ao carregar sessões:', error);
    }
  }, [authenticatedFetch]);

  const loadCategories = useCallback(async () => {
    try {
      const res = await authenticatedFetch('/api/campaigns/tags');
      const data = await res.json();
      setCategories(data || []);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  }, [authenticatedFetch]);

  const loadDirectDataToken = useCallback(async () => {
    try {
      const res = await authenticatedFetch('/api/settings');
      const data = await res.json();
      setHasDirectDataToken(!!(data.directDataApiTokenConfigured || data.directDataApiToken));
    } catch (error) {
      console.error('Erro ao verificar token DirectData:', error);
    }
  }, [authenticatedFetch]);

  const loadLateAccounts = useCallback(async () => {
    try {
      const res = await authenticatedFetch('/api/whatsapp-templates/accounts');
      if (res.ok) {
        const data = await res.json();
        const accounts = data.accounts || data.data || data || [];
        setLateAccounts(Array.isArray(accounts) ? accounts : []);
      }
    } catch (error) {
      console.error('Erro ao carregar contas WhatsApp Oficial:', error);
    }
  }, [authenticatedFetch]);

  const loadLateTemplates = useCallback(async (accountId: string) => {
    if (!accountId) { setLateTemplates([]); return; }
    setLoadingTemplates(true);
    try {
      const res = await authenticatedFetch(`/api/whatsapp-templates/templates?accountId=${accountId}`);
      if (res.ok) {
        const data = await res.json();
        const templates = data.templates || data.data || data;
        setLateTemplates(Array.isArray(templates) ? templates : []);
      } else {
        setLateTemplates([]);
      }
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
      setLateTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    loadAutomations();
    loadSessions();
    loadCategories();
    loadDirectDataToken();
    loadLateAccounts();
  }, [loadAutomations, loadSessions, loadCategories, loadDirectDataToken, loadLateAccounts]);

  const resetForm = () => {
    setFormName('');
    setFormEnabled(false);
    setFormSessionNames([]);
    setFormSituacao('entregue');
    setFormFilterType('descricao');
    setFormFilterValues(['']);
    setFormSequence([]);
    setUploadingFiles({});
    setFileInfos({});
    setDraggedIndex(null);
    setFormRandomDelay(30);
    setFormRandomDelayMax(80);
    setFormScheduleStartDay(null);
    setFormScheduleEndDay(null);
    setFormScheduleStartHour(null);
    setFormScheduleEndHour(null);
    setFormCategoryId('');
    setFormContactTags([]);
    setUseOfficialApi(false);
    setFormLateAccountId('');
    setFormLateTemplateName('');
    setFormLateTemplateLang('pt_BR');
    setFormTemplateText('');
    setFormTemplateVarOrder([]);
    setLateTemplates([]);
    setEditingId(null);
    setShowForm(false);
  };

  const editAutomation = (auto: TinyAutomation) => {
    setFormName(auto.name);
    setFormEnabled(auto.enabled);
    setFormSituacao(auto.situacao || 'entregue');
    setFormFilterType(auto.filterType as 'descricao' | 'sku');
    setFormFilterValues(auto.filterValues.length > 0 ? auto.filterValues : ['']);
    setUploadingFiles({});
    setFileInfos({});
    setFormRandomDelay(auto.randomDelay);
    setFormRandomDelayMax(auto.randomDelayMax);
    setFormScheduleStartDay(auto.scheduleStartDay);
    setFormScheduleEndDay(auto.scheduleEndDay);
    setFormScheduleStartHour(auto.scheduleStartHour);
    setFormScheduleEndHour(auto.scheduleEndHour);
    setFormCategoryId(auto.categoryId || '');
    setFormContactTags(auto.contactTags || []);

    if (auto.messageType === 'whatsapp_template') {
      setUseOfficialApi(true);
      setFormSessionNames([]);
      setFormSequence([]);
      setFormLateAccountId(auto.lateAccountId || '');
      setFormLateTemplateName(auto.lateTemplateName || '');
      setFormLateTemplateLang(auto.lateTemplateLang || 'pt_BR');
      const editText = typeof auto.messageContent === 'string' ? auto.messageContent : (auto.messageContent?.text || '');
      setFormTemplateText(editText);
      setFormTemplateVarOrder(auto.messageContent?.variableOrder || extractVariableOrder(editText));
      if (auto.lateAccountId) loadLateTemplates(auto.lateAccountId);
    } else {
      setUseOfficialApi(false);
      setFormSessionNames(auto.sessionNames);
      setFormLateAccountId('');
      setFormLateTemplateName('');
      setFormLateTemplateLang('pt_BR');
      setFormTemplateText('');
      setLateTemplates([]);
      if (auto.messageType === 'sequence' && auto.messageContent?.sequence) {
        setFormSequence(auto.messageContent.sequence);
      } else {
        setFormSequence([{ type: auto.messageType, content: auto.messageContent }]);
      }
    }

    setEditingId(auto.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      toast.error('Nome da regra é obrigatório');
      return;
    }

    const cleanValues = formFilterValues.filter(v => v.trim() !== '');
    if (cleanValues.length === 0) {
      toast.error('Adicione pelo menos um valor de filtro');
      return;
    }

    if (useOfficialApi) {
      if (!formLateAccountId) { toast.error('Selecione uma conta WhatsApp Oficial'); return; }
      if (!formLateTemplateName) { toast.error('Selecione um template'); return; }
      if (!formTemplateText.trim()) { toast.error('O corpo do template não pode estar vazio'); return; }
      if (!selectedLateTemplate || !isTemplateCompatibleWithTinyAutomation(selectedLateTemplate)) {
        toast.error('Selecione um template compatível com automação ERP');
        return;
      }
    } else {
      if (formSessionNames.length === 0) { toast.error('Selecione pelo menos uma conexão WhatsApp'); return; }
      if (formSequence.length === 0) { toast.error('Adicione pelo menos uma mensagem'); return; }
    }

    try {
      let finalMessageType: string;
      let finalMessageContent: any;

      if (useOfficialApi) {
        finalMessageType = 'whatsapp_template';
        finalMessageContent = { text: formTemplateText, variableOrder: formTemplateVarOrder };
      } else {
        const isSequence = formSequence.length > 1;
        if (isSequence) {
          finalMessageType = 'sequence';
          finalMessageContent = { sequence: formSequence };
        } else {
          finalMessageType = formSequence[0].type;
          finalMessageContent = formSequence[0].content;
        }
      }

      const body: any = {
        name: formName,
        enabled: formEnabled,
        sessionNames: useOfficialApi ? [] : formSessionNames,
        situacao: formSituacao,
        filterType: formFilterType,
        filterValues: cleanValues,
        messageType: finalMessageType,
        messageContent: finalMessageContent,
        randomDelay: formRandomDelay,
        randomDelayMax: formRandomDelayMax,
        scheduleStartDay: formScheduleStartDay,
        scheduleEndDay: formScheduleEndDay,
        scheduleStartHour: formScheduleStartHour,
        scheduleEndHour: formScheduleEndHour,
        categoryId: formCategoryId || null,
        contactTags: formContactTags,
        lateAccountId: useOfficialApi ? formLateAccountId : null,
        lateTemplateName: useOfficialApi ? formLateTemplateName : null,
        lateTemplateLang: useOfficialApi ? formLateTemplateLang : null,
      };

      let res;
      if (editingId) {
        res = await authenticatedFetch(`/api/tiny/automations/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        });
      } else {
        res = await authenticatedFetch('/api/tiny/automations', {
          method: 'POST',
          body: JSON.stringify(body),
        });
      }

      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error || 'Erro ao salvar automação');
        return;
      }

      toast.success(editingId ? 'Automação atualizada!' : 'Automação criada!');
      resetForm();
      loadAutomations();
    } catch (error) {
      toast.error('Erro ao salvar automação');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir esta regra de automação?')) return;

    try {
      const res = await authenticatedFetch(`/api/tiny/automations/${id}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Automação excluída');
        loadAutomations();
      } else {
        toast.error('Erro ao excluir automação');
      }
    } catch (error) {
      toast.error('Erro ao excluir automação');
    }
  };

  const doToggle = async (auto: TinyAutomation, nextEnabled: boolean) => {
    try {
      const res = await authenticatedFetch(`/api/tiny/automations/${auto.id}`, {
        method: 'PUT',
        body: JSON.stringify({ enabled: nextEnabled }),
      });
      if (res.ok) {
        toast.success(nextEnabled ? 'Automação ativada' : 'Automação desativada');
        loadAutomations();
      }
    } catch (error) {
      toast.error('Erro ao alterar automação');
    }
  };

  const handleToggle = async (auto: TinyAutomation) => {
    const nextEnabled = !auto.enabled;
    // Créditos: ativar a automação passa a recuperar contatos automaticamente, cobrando por consulta.
    if (nextEnabled) {
      requestCreditConfirm({
        title: 'Ativar automação de recuperação de contatos',
        items: [{ featureKey: 'erp_contact_recovery', qty: 1, label: 'consulta' }],
        note: 'Enquanto ativa, o sistema recupera contatos dos novos pedidos automaticamente e consome este valor POR consulta ao DirectData (pedidos que já têm telefone/contato são pulados). Quanto mais pedidos, maior o gasto.',
        confirmLabel: 'Ativar automação',
        onConfirm: () => { doToggle(auto, true); },
      });
      return;
    }
    doToggle(auto, false);
  };

  const addFilterValue = () => {
    if (formFilterValues.length >= 5) {
      toast.error('Máximo de 5 valores de filtro');
      return;
    }
    setFormFilterValues([...formFilterValues, '']);
  };

  const removeFilterValue = (index: number) => {
    if (formFilterValues.length <= 1) return;
    setFormFilterValues(formFilterValues.filter((_, i) => i !== index));
  };

  const updateFilterValue = (index: number, value: string) => {
    const updated = [...formFilterValues];
    updated[index] = value;
    setFormFilterValues(updated);
  };

  const toggleSession = (name: string) => {
    if (formSessionNames.includes(name)) {
      setFormSessionNames(formSessionNames.filter(n => n !== name));
    } else {
      setFormSessionNames([...formSessionNames, name]);
    }
  };

  // Sequence helpers
  const updateSequenceContent = (index: number, contentUpdates: any) => {
    setFormSequence(prev => prev.map((item, i) =>
      i === index ? { ...item, content: { ...item.content, ...contentUpdates } } : item
    ));
  };

  const removeSequenceItem = (index: number) => {
    setFormSequence(prev => prev.filter((_, i) => i !== index));
    setUploadingFiles(prev => { const c = { ...prev }; delete c[index]; return c; });
    setFileInfos(prev => { const c = { ...prev }; delete c[index]; return c; });
  };

  const handleFileUpload = async (file: File, index: number) => {
    setUploadingFiles(prev => ({ ...prev, [index]: true }));
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      const token = localStorage.getItem('auth_token');
      const headers: HeadersInit = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch('/api/media/upload', {
        method: 'POST',
        body: uploadFormData,
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Erro ao fazer upload do arquivo');
      }

      const data = await response.json();
      setFileInfos(prev => ({ ...prev, [index]: { name: data.originalName, size: data.size, type: data.mimetype } }));
      updateSequenceContent(index, { url: data.fileUrl });
      toast.success('Arquivo carregado com sucesso!');
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao fazer upload do arquivo');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [index]: false }));
    }
  };

  const handleRemoveFile = (index: number) => {
    updateSequenceContent(index, { url: '' });
    setFileInfos(prev => { const c = { ...prev }; delete c[index]; return c; });
    toast.success('Arquivo removido');
  };

  const handleDragStart = (index: number) => setDraggedIndex(index);
  const handleDragOver = (e: React.DragEvent) => e.preventDefault();
  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) { setDraggedIndex(null); return; }
    setFormSequence(prev => {
      const newSeq = [...prev];
      const [dragged] = newSeq.splice(draggedIndex, 1);
      newSeq.splice(dropIndex, 0, dragged);
      return newSeq;
    });
    setDraggedIndex(null);
  };
  const handleDragEnd = () => setDraggedIndex(null);

  function getTemplateBodyText(template: LateTemplate): string {
    const bodyComponent = template.components?.find(c => c.type === 'BODY' || c.type === 'body');
    return bodyComponent?.text || '';
  }

  function getTemplateVariables(template: LateTemplate): string[] {
    return template.variableOrder || extractVariableOrder(getTemplateBodyText(template));
  }

  function getTemplateIncompatibleVariables(template: LateTemplate): string[] {
    const variables = getTemplateVariables(template);
    return variables.filter(key => CONTACT_ONLY_TEMPLATE_VARIABLE_KEYS.has(key));
  }

  function getTemplateUnknownVariables(template: LateTemplate): string[] {
    const variables = getTemplateVariables(template);
    return variables.filter(key => !ERP_TEMPLATE_VARIABLE_KEYS.has(key) && !CONTACT_ONLY_TEMPLATE_VARIABLE_KEYS.has(key));
  }

  function isTemplateCompatibleWithTinyAutomation(template: LateTemplate): boolean {
    return getTemplateIncompatibleVariables(template).length === 0 && getTemplateUnknownVariables(template).length === 0;
  }

  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
      {creditConfirmModal}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Configurar Envio Automático de Mensagem</h2>
          <p className="text-sm text-gray-500">
            Configure regras para enviar mensagens automaticamente quando pedidos entregues corresponderem aos filtros.
            A cada 30 minutos o sistema verifica novos pedidos entregues.
          </p>
        </div>
        {automations.length < 5 && !showForm && (
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm font-medium whitespace-nowrap"
          >
            + Nova Regra
          </button>
        )}
      </div>

      {/* DirectData Token Status */}
      <div className={`border rounded-lg p-4 mb-4 ${hasDirectDataToken ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
        <h3 className={`text-sm font-medium mb-1 ${hasDirectDataToken ? 'text-green-800' : 'text-yellow-800'}`}>
          🔑 {hasDirectDataToken ? '(Consome crédito)' : '(Não configurado)'}
        </h3>
        <p className="text-xs text-gray-600">
          Quando um pedido não tem telefone, o sistema busca o número via CPF/CNPJ.
          Cada consulta consome créditos (o valor é exibido ao ativar a regra).
        </p>
        {!hasDirectDataToken && (
          <p className="text-xs text-yellow-700 mt-1 font-medium">
            ⚠️ Configure o token DirectData nas <strong>Configurações do Sistema</strong> (menu Configurações) para habilitar a busca automática de telefone.
          </p>
        )}
      </div>

      {/* Botão Processar Agora */}
      {automations.length > 0 && (
        <div className="mb-4">
          <button
            onClick={() => requestCreditConfirm({
              title: 'Processar regras agora',
              items: [{ featureKey: 'erp_contact_recovery', qty: 1, label: 'consulta' }],
              note: 'Processa os pedidos elegíveis agora, consumindo este valor POR consulta ao DirectData (pedidos que já têm telefone/contato são pulados).',
              confirmLabel: 'Processar',
              onConfirm: async () => {
                setProcessingNow(true);
                try {
                  const res = await authenticatedFetch('/api/tiny/automations/process-now', { method: 'POST' });
                  const data = await res.json();
                  if (res.ok) {
                    toast.success(data.message || 'Processamento iniciado!');
                  } else {
                    toast.error(data.error || 'Erro ao processar');
                  }
                } catch (error) {
                  toast.error('Erro ao processar regras');
                } finally {
                  setProcessingNow(false);
                }
              },
            })}
            disabled={processingNow}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm font-medium flex items-center gap-2"
          >
            {processingNow ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processando...
              </>
            ) : (
              <>
                ▶️ Processar regras agora
              </>
            )}
          </button>
        </div>
      )}

      {/* Lista de automações existentes */}
      {loading ? (
        <div className="text-center py-8 text-gray-500 text-sm">Carregando automações...</div>
      ) : automations.length === 0 && !showForm ? (
        <div className="text-center py-8 text-gray-400 text-sm">
          Nenhuma regra de automação configurada. Clique em "+ Nova Regra" para começar.
        </div>
      ) : (
        <div className="space-y-3 mb-4">
          {automations.map((auto) => (
            <div key={auto.id} className={`border rounded-lg p-4 ${auto.enabled ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <button
                    onClick={() => handleToggle(auto)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${auto.enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${auto.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">{auto.name}</h4>
                    <p className="text-xs text-gray-500">
                      Situação: {auto.situacao === 'aberto' ? 'Em aberto' : auto.situacao === 'aprovado' ? 'Aprovado' : auto.situacao === 'preparando_envio' ? 'Preparando envio' : auto.situacao === 'faturado' ? 'Faturado' : auto.situacao === 'pronto_envio' ? 'Pronto para envio' : auto.situacao === 'enviado' ? 'Enviado' : auto.situacao === 'entregue' ? 'Entregue' : auto.situacao === 'nao_entregue' ? 'Não Entregue' : auto.situacao === 'cancelado' ? 'Cancelado' : 'Entregue'} | {auto.filterType === 'descricao' ? 'Descrição' : 'SKU'} contém: {auto.filterValues.join(' OU ')}
                    </p>
                    <p className="text-xs text-gray-400">
                      {auto.messageType === 'whatsapp_template' ? (
                        <>✅ API Oficial | Template: <strong>{auto.lateTemplateName || '—'}</strong></>
                      ) : (
                        <>
                          Sessões: {auto.sessionNames.join(', ')} | {
                            auto.messageType === 'sequence' && auto.messageContent?.sequence
                              ? `${auto.messageContent.sequence.length} mensagem(ns)`
                              : `Tipo: ${MESSAGE_TYPES.find(t => t.value === auto.messageType)?.label || auto.messageType}`
                          }
                        </>
                      )}
                      {auto.category && ` | Categoria: ${auto.category.nome}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => editAutomation(auto)}
                    className="px-3 py-1 text-xs text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => handleDelete(auto.id)}
                    className="px-3 py-1 text-xs text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100"
                  >
                    Excluir
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Formulário de criação/edição */}
      {showForm && (
        <div className="border border-indigo-200 rounded-lg p-5 bg-indigo-50/30">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">
            {editingId ? 'Editar Regra de Automação' : 'Nova Regra de Automação'}
          </h3>

          <div className="space-y-4">
            {/* Nome e Habilitado */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nome da Regra</label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  placeholder="Ex: Pós-venda Produto X"
                />
              </div>
              <div className="flex items-center gap-3 pt-5">
                <button
                  onClick={() => setFormEnabled(!formEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
                <span className="text-sm text-gray-700">{formEnabled ? 'Habilitado' : 'Desabilitado'}</span>
              </div>
            </div>

            {/* Tipo de Envio: Não-oficial vs API Oficial */}
            <div className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <span className="text-xs font-medium text-gray-600">Tipo de envio:</span>
              <button
                type="button"
                onClick={() => { setUseOfficialApi(false); setFormLateAccountId(''); setFormLateTemplateName(''); setFormTemplateText(''); setLateTemplates([]); }}
                className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                  !useOfficialApi ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                📱 WhatsApp Não-oficial
              </button>
              <button
                type="button"
                onClick={() => { setUseOfficialApi(true); setFormSessionNames([]); setFormSequence([]); }}
                className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                  useOfficialApi ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                ✅ API Oficial WhatsApp
              </button>
            </div>

            {/* Conexão WhatsApp Não-oficial */}
            {!useOfficialApi && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Conexão WhatsApp</label>
                <div className="flex flex-wrap gap-2">
                  {sessions.map((session) => (
                    <button
                      key={session.name}
                      onClick={() => toggleSession(session.name)}
                      className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                        formSessionNames.includes(session.name)
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {session.displayName || session.name}
                    </button>
                  ))}
                  {sessions.length === 0 && (
                    <span className="text-xs text-red-500">Nenhuma sessão WhatsApp ativa encontrada</span>
                  )}
                </div>
              </div>
            )}

            {/* API Oficial: Conta + Template */}
            {useOfficialApi && (
              <div className="space-y-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-green-700 text-sm font-medium">✅ API Oficial WhatsApp</span>
                  <span className="text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded-full">Requer template aprovado no Meta</span>
                </div>

                {/* Seletor de Conta */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Conta WhatsApp Oficial</label>
                  <select
                    value={formLateAccountId}
                    onChange={(e) => {
                      setFormLateAccountId(e.target.value);
                      setFormLateTemplateName('');
                      setFormTemplateText('');
                      loadLateTemplates(e.target.value);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">Selecione a conta...</option>
                    {lateAccounts.map((acc) => (
                      <option key={acc.id} value={acc.platformAccountId || acc.id}>
                        {acc.accountName}{acc.accountUsername ? ` (@${acc.accountUsername})` : ''}{acc.groupName ? ` - ${acc.groupName}` : ''}
                      </option>
                    ))}
                  </select>
                  {lateAccounts.length === 0 && (
                    <p className="text-xs text-yellow-600 mt-1">⚠️ Nenhuma conta encontrada. Configure em <strong>WhatsApp Oficial</strong>.</p>
                  )}
                </div>

                {/* Seletor de Template */}
                {formLateAccountId && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Template</label>
                    {loadingTemplates ? (
                      <div className="text-xs text-gray-500 py-2">Carregando templates...</div>
                    ) : (
                      <select
                        value={formLateTemplateName}
                        onChange={(e) => {
                          const selected = lateTemplates.find(t => t.name === e.target.value);
                          if (selected && !isTemplateCompatibleWithTinyAutomation(selected)) {
                            const invalidKeys = [
                              ...getTemplateIncompatibleVariables(selected),
                              ...getTemplateUnknownVariables(selected)
                            ];
                            toast.error(`Este template não pode ser usado na automação ERP: ${invalidKeys.join(', ')}`);
                            setFormLateTemplateName('');
                            setFormLateTemplateLang('pt_BR');
                            setFormTemplateText('');
                            setFormTemplateVarOrder([]);
                            return;
                          }
                          setFormLateTemplateName(e.target.value);
                          if (selected) setFormLateTemplateLang(selected.language || 'pt_BR');
                          const bodyText = selected ? getTemplateBodyText(selected) : '';
                          setFormTemplateText(bodyText);
                          setFormTemplateVarOrder(selected ? getTemplateVariables(selected) : []);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="">Selecione o template...</option>
                        {lateTemplates.map((t) => (
                          <option key={t.id || t.name} value={t.name} disabled={!isTemplateCompatibleWithTinyAutomation(t)}>
                            {t.name} ({t.language}) {t.status !== 'APPROVED' ? `— ${t.status}` : '✓'}
                            {!isTemplateCompatibleWithTinyAutomation(t) ? ' — incompatível com ERP' : ''}
                          </option>
                        ))}
                      </select>
                    )}
                    {lateTemplates.length === 0 && !loadingTemplates && (
                      <p className="text-xs text-gray-400 mt-1">Nenhum template encontrado para esta conta.</p>
                    )}
                  </div>
                )}

                {/* Corpo do template com variáveis */}
                {formLateTemplateName && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Corpo do Template</label>
                    <textarea
                      value={formTemplateText}
                      rows={4}
                      readOnly
                      className="w-full px-3 py-2 border border-green-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                    />
                    {selectedTemplateIncompatibleVariables.length > 0 && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-xs font-medium text-red-700">Template incompatível com automação ERP</p>
                        <p className="text-xs text-red-600 mt-1">Variáveis exclusivas de contato: {selectedTemplateIncompatibleVariables.map((key) => TINY_VARIABLE_LABELS[key] || key).join(', ')}</p>
                      </div>
                    )}
                    {selectedTemplateUnknownVariables.length > 0 && (
                      <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                        <p className="text-xs font-medium text-yellow-700">Template com variáveis não reconhecidas</p>
                        <p className="text-xs text-yellow-600 mt-1">Variáveis encontradas: {selectedTemplateUnknownVariables.map((key) => TINY_VARIABLE_LABELS[key] || key).join(', ')}</p>
                      </div>
                    )}
                    {formTemplateVarOrder.length > 0 && (
                      <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                        <p className="text-xs font-medium text-blue-700 mb-1">Mapeamento de variáveis (ordem de envio):</p>
                        <div className="flex flex-col gap-0.5">
                          {formTemplateVarOrder.map((key, i) => (
                            <span key={key} className="text-xs text-blue-600">
                              <span className="font-mono bg-blue-100 px-1 rounded">{`{{${i + 1}}}`}</span>
                              {' → '}
                              <span className="font-medium">{TINY_VARIABLE_LABELS[key] || key}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Situação do Pedido */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Situação do Pedido</label>
              <select
                value={formSituacao}
                onChange={(e) => setFormSituacao(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="aberto">Em aberto</option>
                <option value="aprovado">Aprovado</option>
                <option value="preparando_envio">Preparando envio</option>
                <option value="faturado">Faturado (atendido)</option>
                <option value="pronto_envio">Pronto para envio</option>
                <option value="enviado">Enviado</option>
                <option value="entregue">Entregue</option>
                <option value="nao_entregue">Não Entregue</option>
                <option value="cancelado">Cancelado</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">A automação só processará pedidos com essa situação.</p>
            </div>

            {/* Filtro */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Filtro do Pedido</label>
              <div className="flex gap-2 mb-2">
                <select
                  value={formFilterType}
                  onChange={(e) => setFormFilterType(e.target.value as 'descricao' | 'sku')}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="descricao">Descrição contém</option>
                  <option value="sku">SKU contém</option>
                </select>
              </div>
              <div className="space-y-2">
                {formFilterValues.map((val, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    {idx > 0 && <span className="text-xs text-gray-400 font-medium">OU</span>}
                    <input
                      type="text"
                      value={val}
                      onChange={(e) => updateFilterValue(idx, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder={`Valor ${idx + 1}`}
                    />
                    {formFilterValues.length > 1 && (
                      <button
                        onClick={() => removeFilterValue(idx)}
                        className="text-red-500 hover:text-red-700 text-sm px-2"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                {formFilterValues.length < 5 && (
                  <button
                    onClick={addFilterValue}
                    className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                  >
                    + Adicionar outro valor (OU)
                  </button>
                )}
              </div>
            </div>

            {/* Sequência de Mensagens — apenas para não-oficial */}
            {!useOfficialApi && <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-medium text-gray-600">Mensagens</label>
                <button
                  type="button"
                  onClick={() => setFormSequence(prev => [...prev, { type: 'text', content: { text: '' } }])}
                  className="px-3 py-1.5 bg-indigo-600 text-white text-xs rounded-md hover:bg-indigo-700 flex items-center gap-1"
                >
                  <span className="text-sm">+</span> Mensagem
                </button>
              </div>

              <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-700">
                  <strong>Variáveis:</strong>{' '}
                  <code className="bg-blue-100 px-1 rounded">{'{{nome}}'}</code>{' '}
                  <code className="bg-blue-100 px-1 rounded">{'{{itens}}'}</code>{' '}
                  <code className="bg-blue-100 px-1 rounded">{'{{numeropedido}}'}</code>{' '}
                  <code className="bg-blue-100 px-1 rounded">{'{{numeronotafiscal}}'}</code>{' '}
                  <code className="bg-blue-100 px-1 rounded">{'{{ecommerce}}'}</code>
                </p>
              </div>

              {formSequence.length === 0 ? (
                <div className="text-center py-6 text-gray-400 text-sm bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <div className="text-2xl mb-2">📝</div>
                  Nenhuma mensagem. Clique em "+ Mensagem" para adicionar.
                </div>
              ) : (
                <div className="space-y-3">
                  {formSequence.map((item, index) => (
                    <div
                      key={index}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`border rounded-lg p-3 transition-all cursor-move ${
                        draggedIndex === index ? 'border-indigo-500 bg-indigo-50 opacity-50' : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400 cursor-move text-lg">⋮⋮</span>
                          <span className="text-xs font-medium text-gray-600">Mensagem {index + 1}</span>
                        </div>
                        <button type="button" onClick={() => removeSequenceItem(index)} className="text-xs text-red-600 hover:text-red-800">
                          Remover
                        </button>
                      </div>

                      <div className="space-y-2">
                        <select
                          value={item.type}
                          onChange={(e) => {
                            const newType = e.target.value;
                            setFormSequence(prev => prev.map((s, i) =>
                              i === index ? { type: newType, content: getDefaultContent(newType) } : s
                            ));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          {MESSAGE_TYPES.map((t) => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </select>

                        {/* Text */}
                        {item.type === 'text' && (
                          <div>
                            <textarea
                              value={item.content.text || ''}
                              onChange={(e) => updateSequenceContent(index, { text: e.target.value })}
                              rows={3}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              placeholder="Digite sua mensagem... Use {{nome}}, {{itens}}, {{numeropedido}}, etc."
                            />
                            <div className="flex flex-wrap gap-1 mt-1">
                              {['{{nome}}', '{{itens}}', '{{numeropedido}}', '{{numeronotafiscal}}', '{{ecommerce}}'].map((v) => (
                                <button
                                  key={v}
                                  type="button"
                                  onClick={() => updateSequenceContent(index, { text: (item.content.text || '') + v })}
                                  className="px-2 py-0.5 bg-green-100 text-green-600 text-xs rounded hover:bg-green-200"
                                >
                                  {v}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Image / Video / Audio / Document - File Upload */}
                        {['image', 'video', 'audio', 'document'].includes(item.type) && (
                          <div className="space-y-2">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                              <input
                                type="file"
                                accept={
                                  item.type === 'image' ? 'image/*' :
                                  item.type === 'video' ? 'video/*' :
                                  item.type === 'audio' ? 'audio/*' : '*'
                                }
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleFileUpload(file, index);
                                }}
                                className="block text-sm text-gray-700"
                              />
                              {item.content.url && (
                                <button type="button" onClick={() => handleRemoveFile(index)} className="text-xs text-red-600 hover:text-red-700">
                                  Remover arquivo
                                </button>
                              )}
                            </div>

                            {uploadingFiles[index] && (
                              <p className="text-xs text-gray-500">Carregando arquivo...</p>
                            )}

                            {fileInfos[index] && (
                              <div className="flex items-center justify-between rounded bg-gray-50 p-2 text-xs text-gray-700">
                                <span>{getFileIcon(fileInfos[index].type)} {fileInfos[index].name}</span>
                                <span className="text-gray-500">{formatFileSize(fileInfos[index].size)}</span>
                              </div>
                            )}

                            {!item.content.url && !uploadingFiles[index] && (
                              <input
                                type="text"
                                value={item.content.url || ''}
                                onChange={(e) => updateSequenceContent(index, { url: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="Ou cole a URL do arquivo aqui..."
                              />
                            )}

                            {['image', 'video', 'document'].includes(item.type) && (
                              <div>
                                <textarea
                                  value={item.content.caption || ''}
                                  onChange={(e) => updateSequenceContent(index, { caption: e.target.value })}
                                  rows={2}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                  placeholder="Legenda (opcional) - Use {{nome}}, {{itens}}, etc."
                                />
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {['{{nome}}', '{{itens}}', '{{numeropedido}}', '{{numeronotafiscal}}', '{{ecommerce}}'].map((v) => (
                                    <button
                                      key={v}
                                      type="button"
                                      onClick={() => updateSequenceContent(index, { caption: (item.content.caption || '') + v })}
                                      className="px-2 py-0.5 bg-green-100 text-green-600 text-xs rounded hover:bg-green-200"
                                    >
                                      {v}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                            {item.type === 'document' && (
                              <input
                                type="text"
                                value={item.content.fileName || ''}
                                onChange={(e) => updateSequenceContent(index, { fileName: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="Nome do arquivo (opcional)"
                              />
                            )}
                          </div>
                        )}

                        {/* OpenAI */}
                        {item.type === 'openai' && (
                          <div className="space-y-3">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                              <p className="text-xs text-blue-700"><strong>🤖 OpenAI</strong> — Gera mensagens personalizadas usando IA</p>
                            </div>
                            <p className="text-xs text-gray-500">Modelo: <strong>IntegraIA (cx/gpt-6-luna)</strong> — definido pelo sistema.</p>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Prompt do Sistema *</label>
                              <textarea
                                value={item.content.system || ''}
                                onChange={(e) => updateSequenceContent(index, { system: e.target.value })}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="Ex: Você é um assistente de pós-venda. Responda de forma profissional..."
                              />
                              <div className="flex flex-wrap gap-1 mt-1">
                                {['{{nome}}', '{{itens}}', '{{numeropedido}}', '{{numeronotafiscal}}', '{{ecommerce}}'].map((v) => (
                                  <button
                                    key={v}
                                    type="button"
                                    onClick={() => updateSequenceContent(index, { system: (item.content.system || '') + v })}
                                    className="px-2 py-0.5 bg-green-100 text-green-600 text-xs rounded hover:bg-green-200"
                                  >
                                    {v}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Prompt do Usuário *</label>
                              <textarea
                                value={item.content.user || ''}
                                onChange={(e) => updateSequenceContent(index, { user: e.target.value })}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="Ex: Crie uma mensagem de pós-venda para {{nome}} que comprou {{itens}}..."
                              />
                              <div className="flex flex-wrap gap-1 mt-1">
                                {['{{nome}}', '{{itens}}', '{{numeropedido}}', '{{ecommerce}}'].map((v) => (
                                  <button
                                    key={v}
                                    type="button"
                                    onClick={() => updateSequenceContent(index, { user: (item.content.user || '') + v })}
                                    className="px-2 py-0.5 bg-green-100 text-green-600 text-xs rounded hover:bg-green-200"
                                  >
                                    {v}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Wait */}
                        {item.type === 'wait' && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                            <div className="flex items-center gap-3">
                              <span className="text-xl">⏱️</span>
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  min="1"
                                  max="3600"
                                  value={item.content.waitTime || 30}
                                  onChange={(e) => updateSequenceContent(index, { waitTime: parseInt(e.target.value) || 30 })}
                                  className="w-20 px-2 py-1.5 border border-yellow-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white"
                                />
                                <span className="text-xs text-yellow-700">segundos de espera</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>}

            {/* Randomização */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Intervalo de Randomização (segundos)</label>
              <div className="flex items-center gap-2">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Mínimo</label>
                  <input
                    type="number"
                    value={formRandomDelay}
                    onChange={(e) => setFormRandomDelay(parseInt(e.target.value) || 0)}
                    min={0}
                    max={300}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <span className="text-gray-400 font-medium mt-5">a</span>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Máximo</label>
                  <input
                    type="number"
                    value={formRandomDelayMax ?? formRandomDelay}
                    onChange={(e) => setFormRandomDelayMax(parseInt(e.target.value) || 0)}
                    min={0}
                    max={300}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-1">Delay aleatório entre {formRandomDelay}s e {formRandomDelayMax ?? formRandomDelay}s antes de cada envio</p>
            </div>

            {/* Horários e Dias */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">Horários e Dias de Disparo (opcional)</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Dia Início</label>
                  <select
                    value={formScheduleStartDay ?? ''}
                    onChange={(e) => setFormScheduleStartDay(e.target.value === '' ? null : parseInt(e.target.value))}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs"
                  >
                    <option value="">Sem filtro</option>
                    {DAY_NAMES.map((d, i) => <option key={i} value={i}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Dia Fim</label>
                  <select
                    value={formScheduleEndDay ?? ''}
                    onChange={(e) => setFormScheduleEndDay(e.target.value === '' ? null : parseInt(e.target.value))}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs"
                  >
                    <option value="">Sem filtro</option>
                    {DAY_NAMES.map((d, i) => <option key={i} value={i}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Hora Início</label>
                  <select
                    value={formScheduleStartHour ?? ''}
                    onChange={(e) => setFormScheduleStartHour(e.target.value === '' ? null : parseInt(e.target.value))}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs"
                  >
                    <option value="">Sem filtro</option>
                    {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{`${i}:00`}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Hora Fim</label>
                  <select
                    value={formScheduleEndHour ?? ''}
                    onChange={(e) => setFormScheduleEndHour(e.target.value === '' ? null : parseInt(e.target.value))}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs"
                  >
                    <option value="">Sem filtro</option>
                    {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{`${i}:00`}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <ContactCategorySelector categories={categories} value={formCategoryId} onChange={setFormCategoryId} label="Categoria para salvar o contato (opcional)" />
            <ContactLabelSelector value={formContactTags} onChange={setFormContactTags} label="Etiquetas adicionadas pela automacao" />

            {/* Botões */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={handleSave}
                className="px-5 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm font-medium"
              >
                {editingId ? 'Salvar Alterações' : 'Criar Regra'}
              </button>
              <button
                onClick={resetForm}
                className="px-5 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info */}
      <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-xs text-blue-700">
          <strong>ℹ️ Como funciona:</strong> O sistema verifica a cada 30 minutos se há novos pedidos entregues no Tiny.
          Se um pedido corresponder a uma regra ativa, o contato é salvo e a mensagem é enviada automaticamente.
          Se o pedido não tiver telefone, o sistema busca via (CPF/CNPJ) consumindo créditos por consulta (o valor é exibido ao ativar a regra).
        </p>
      </div>
    </div>
  );
}
